import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import useApiData from "../../services/useApiData.js";
import PostData from "../../services/PostData.js";
import PutData from "../../services/PutData.js";
import api from "../../services/axiosInterceptor.js";
import GetData from "../../services/GetData.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import ConfirmDeleteDialog from "../Helpers/ConfirmDeleteDialog.jsx";
import TabNavigation from "../TabNavigation.jsx";
import { SingleSelectField } from "./TypedFields.jsx";
import DetailField from "./DetailField.jsx";
import LinkedCatalogItems from "./LinkedCatalogItems.jsx";
import { deliveryStatusChipSx } from "./invoiceDeliveryStatus.js";
import { editableFormSnapshot } from "./invoiceFormUtils.js";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { INVOICE_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import { mergeDataGridSx } from "../Helpers/dataGridStyles.js";
import {
    catalogDataGridSx,
    catalogDangerButtonSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "./catalogGridStyles.js";
import FloatingAssetPreview from "../Helpers/FloatingAssetPreview.jsx";

/** @typedef {'view' | 'edit' | 'create'} PanelMode */

const INVOICE_TABS = [
    { label: "Incoming", value: "incoming" },
    { label: "Received", value: "received" },
];

const EMPTY_FORM = {
    invoiceNumber: "",
    supplierName: "",
    deliveryStatus: "",
    received: false,
    receivedDate: "",
    trackingId: "",
    invoiceDate: "",
    invoiceAutomationUploadDate: "",
    invoiceProcessedBy: "",
    numberOfItemsOnInvoice: "",
    totalInvoiceAmount: "",
    parserItemIdsOnInvoice: "",
    statusRollupForAutomation: "",
    invoiceDateRollup: "",
    invoicePhoto: null,
    linkedItemSummary: "",
};

const invoiceGridSx = mergeDataGridSx(catalogDataGridSx, {
    "& .invoice-panel-active-row": {
        backgroundColor: "rgba(13, 148, 136, 0.12) !important",
        boxShadow: "inset 3px 0 0 #0d9488",
        "&:hover": {
            backgroundColor: "rgba(13, 148, 136, 0.16) !important",
        },
    },
});

function formatDateDisplay(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
}

function formatDateInput(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
}

function formatMoney(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function joinList(value) {
    if (value == null || value === "") return "";
    if (Array.isArray(value)) return value.filter(Boolean).join(", ");
    return String(value);
}

function daysSinceOrdered(value) {
    if (!value) return null;
    const ordered = new Date(value);
    if (Number.isNaN(ordered.getTime())) return null;
    const start = new Date(ordered);
    start.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((today.getTime() - start.getTime()) / 86400000);
}

function buildFormFromInvoice(inv) {
    const photo = inv.invoicePhoto?.[0];
    const linked =
        inv.catalogItems?.map((c) => c.itemId || c.parserItemId).filter(Boolean) || [];
    return {
        invoiceNumber: inv.invoiceNumber || "",
        supplierName: inv.supplierName || "",
        deliveryStatus: inv.deliveryStatus || "",
        received: Boolean(inv.received),
        receivedDate: formatDateInput(inv.receivedDate),
        trackingId: inv.trackingId || "",
        invoiceDate: formatDateInput(inv.invoiceDate),
        invoiceAutomationUploadDate: formatDateInput(inv.invoiceAutomationUploadDate),
        invoiceProcessedBy: inv.invoiceProcessedBy || "",
        numberOfItemsOnInvoice:
            inv.numberOfItemsOnInvoice == null ? "" : String(inv.numberOfItemsOnInvoice),
        totalInvoiceAmount:
            inv.totalInvoiceAmount == null ? "" : String(inv.totalInvoiceAmount),
        parserItemIdsOnInvoice: joinList(inv.parserItemIdsOnInvoice),
        statusRollupForAutomation: joinList(inv.statusRollupForAutomation),
        invoiceDateRollup: joinList(
            (inv.invoiceDateRollup || []).map((d) => formatDateDisplay(d))
        ),
        invoicePhoto: photo || null,
        linkedItemSummary: linked.length ? linked.join(", ") : "",
    };
}

export default function InvoiceGrid() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const loadedUrlKey = useRef("");

    const urlInvoiceId = searchParams.get("id") || "";
    const urlMode = searchParams.get("mode") || "";
    const urlTab =
        searchParams.get("tab") === "received" ? "received" : "incoming";
    const isIncomingTab = urlTab === "incoming";

    const [rows, setRows] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [rowCount, setRowCount] = useState(0);
    const [selectionModel, setSelectionModel] = useState({
        type: "include",
        ids: new Set(),
    });
    const selectedIds = useMemo(
        () => [...(selectionModel?.ids ?? [])],
        [selectionModel]
    );

    const [panelOpen, setPanelOpen] = useState(false);
    const [panelMode, setPanelMode] = useState("view");
    const [panelLoading, setPanelLoading] = useState(false);
    const [panelInvoice, setPanelInvoice] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formBaseline, setFormBaseline] = useState(editableFormSnapshot(EMPTY_FORM));
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [markingReceivedId, setMarkingReceivedId] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
    const [pendingDismiss, setPendingDismiss] = useState(null);
    const [refreshingRollups, setRefreshingRollups] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const [pdfPreview, setPdfPreview] = useState({
        open: false,
        asset: null,
        title: "",
    });

    const openInvoicePdfPreview = useCallback((invOrAsset, maybeTitle) => {
        const photo = invOrAsset?.invoicePhoto?.[0] || invOrAsset;
        if (!photo?.gcsObject && !photo?.sourceUrl) return;
        const title =
            maybeTitle ||
            (invOrAsset?.invoiceNumber
                ? `Invoice ${invOrAsset.invoiceNumber}`
                : "Invoice PDF");
        setPdfPreview({
            open: true,
            asset: photo,
            title,
        });
    }, []);

    const isEditing = panelMode === "edit" || panelMode === "create";
    const isDirty =
        isEditing && editableFormSnapshot(form) !== formBaseline;
    const activePanelId =
        panelOpen && panelMode !== "create" ? editingId || panelInvoice?._id : null;

    const writeInvoiceUrl = useCallback(
        ({ id = null, mode = null, tab = null } = {}, { replace = false } = {}) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    const nextTab =
                        tab === "received" || tab === "incoming"
                            ? tab
                            : next.get("tab") === "received"
                              ? "received"
                              : "incoming";
                    next.set("tab", nextTab);
                    if (id) next.set("id", String(id));
                    else next.delete("id");
                    if (mode) next.set("mode", mode);
                    else next.delete("mode");
                    return next;
                },
                { replace }
            );
        },
        [setSearchParams]
    );

    const changeTab = useCallback(
        (_event, next) => {
            const tab = next === "received" ? "received" : "incoming";
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setSelectionModel({ type: "include", ids: new Set() });
            writeInvoiceUrl({ tab }, { replace: true });
        },
        [writeInvoiceUrl]
    );

    useEffect(() => {
        const t = setTimeout(() => {
            setSearchQ(searchInput.trim());
            setPaginationModel((prev) =>
                prev.page === 0 ? prev : { ...prev, page: 0 }
            );
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    const { data: fieldMeta } = useApiData("api/broylesllc/catalog-field-meta");
    const invoiceMeta = fieldMeta?.invoice || {};
    const filterFields = useMemo(() => INVOICE_FILTER_FIELDS(invoiceMeta), [invoiceMeta]);
    const {
        apply: applyFilter,
        conditions: gridConditions,
        conjunction: gridConjunction,
        filterActive,
        barProps: filterBar,
    } = useGridFilter(filterFields);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.invoices, {
        sortModel: [{ field: "uploadedDate", sort: "desc" }],
        columnVisibilityModel: {},
    });
    const sortModel = layout.sortModel;

    /** Push Uploaded Date filters to Mongo (createdAt) so they aren't limited to one page. */
    const uploadedDateQuery = useMemo(() => {
        const active = (gridConditions || []).filter(
            (c) =>
                c?.field === "uploadedDate" &&
                c?.operator &&
                (c.operator === "empty" ||
                    c.operator === "notEmpty" ||
                    String(c?.value || "").trim())
        );
        if (!active.length) return {};
        // Only safe to server-push when AND (or a single condition).
        if (gridConjunction === "or" && active.length !== (gridConditions || []).filter((c) => {
            if (!c?.field || !c?.operator) return false;
            if (c.operator === "empty" || c.operator === "notEmpty") return true;
            return Boolean(String(c?.value || "").trim());
        }).length) {
            return {};
        }
        const params = {};
        for (const condition of active) {
            const value = String(condition.value || "").trim().slice(0, 10);
            if (condition.operator === "after" && value) params.uploadedAfter = value;
            else if (condition.operator === "before" && value) params.uploadedBefore = value;
            else if (condition.operator === "is" && value) params.uploadedOn = value;
        }
        return params;
    }, [gridConditions, gridConjunction]);

    const listUrl = useMemo(() => {
        const clientOnlyFilters = filterActive && !Object.keys(uploadedDateQuery).length
            ? true
            : filterActive && gridConjunction === "or";
        const params = new URLSearchParams({
            page: String(clientOnlyFilters ? 1 : paginationModel.page + 1),
            pageSize: String(clientOnlyFilters ? 500 : paginationModel.pageSize),
            received: isIncomingTab ? "false" : "true",
        });
        if (searchQ) params.set("q", searchQ);
        for (const [key, value] of Object.entries(uploadedDateQuery)) {
            params.set(key, value);
        }
        const sort = sortModel?.[0];
        if (sort?.field) {
            params.set("sortBy", sort.field);
            params.set("sortDir", sort.sort === "asc" ? "asc" : "desc");
        }
        return `api/broylesllc/invoices?${params.toString()}`;
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        searchQ,
        filterActive,
        isIncomingTab,
        uploadedDateQuery,
        gridConjunction,
        sortModel,
    ]);

    const { data, error, load, isLoading, refetch } = useApiData(listUrl);

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : 0);
        setRows(
            items.map((inv) => ({
                id: inv._id,
                invoiceNumber: inv.invoiceNumber || "",
                supplierName: inv.supplierName || "",
                deliveryStatus: inv.deliveryStatus || "",
                trackingId: inv.trackingId || "",
                received: Boolean(inv.received),
                receivedDate: formatDateDisplay(inv.receivedDate),
                daysSinceOrdered: daysSinceOrdered(inv.invoiceDate),
                invoiceDate: formatDateDisplay(inv.invoiceDate),
                invoiceAutomationUploadDate: formatDateDisplay(
                    inv.invoiceAutomationUploadDate
                ),
                uploadedDate: formatDateDisplay(inv.createdAt),
                uploadedDateRaw: inv.createdAt || null,
                numberOfItemsOnInvoice: inv.numberOfItemsOnInvoice ?? "",
                totalInvoiceAmount: inv.totalInvoiceAmount ?? "",
                lineItemStatuses: joinList(inv.statusRollupForAutomation),
                raw: inv,
            }))
        );
    }, [data]);

    const filteredRows = useMemo(() => applyFilter(rows), [rows, applyFilter]);

    useEffect(() => {
        if (filterActive) {
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }
    }, [filterActive]);

    const resetPanelState = useCallback(() => {
        setPanelOpen(false);
        setPanelMode("view");
        setPanelInvoice(null);
        setEditingId(null);
        setForm(EMPTY_FORM);
        setFormBaseline(editableFormSnapshot(EMPTY_FORM));
        setPanelLoading(false);
        loadedUrlKey.current = "";
    }, []);

    const applyFormState = useCallback((nextForm) => {
        setForm(nextForm);
        setFormBaseline(editableFormSnapshot(nextForm));
    }, []);

    const loadInvoiceIntoPanel = useCallback(
        async (id, mode = "view") => {
            if (!id) return;
            const key = `${id}:${mode === "edit" ? "edit" : "view"}`;
            loadedUrlKey.current = key;
            setPanelOpen(true);
            setPanelMode(mode === "edit" ? "edit" : "view");
            setEditingId(id);
            setPanelLoading(true);
            setPanelInvoice(null);
            const { getData } = await GetData(`broylesllc/invoices/${id}`);
            setPanelLoading(false);
            if (!getData) {
                loadedUrlKey.current = "";
                resetPanelState();
                writeInvoiceUrl({}, { replace: true });
                setSnack({
                    open: true,
                    color: "error",
                    message: "Could not load invoice.",
                });
                return;
            }
            setPanelInvoice(getData);
            setEditingId(getData._id);
            if (mode === "edit") applyFormState(buildFormFromInvoice(getData));
            const invoiceTab = getData.received ? "received" : "incoming";
            if (searchParams.get("tab") !== invoiceTab) {
                writeInvoiceUrl(
                    { id: getData._id, mode: mode === "edit" ? "edit" : null, tab: invoiceTab },
                    { replace: true }
                );
            }
        },
        [applyFormState, resetPanelState, searchParams, writeInvoiceUrl]
    );

    /** URL is the source of truth — open/close only from `id` / `mode`. */
    useEffect(() => {
        if (urlMode === "create") {
            const key = "create";
            if (loadedUrlKey.current === key) return;
            loadedUrlKey.current = key;
            setPanelOpen(true);
            setPanelMode("create");
            setPanelLoading(false);
            setPanelInvoice(null);
            setEditingId(null);
            applyFormState(EMPTY_FORM);
            return;
        }

        if (urlInvoiceId) {
            const mode = urlMode === "edit" ? "edit" : "view";
            const key = `${urlInvoiceId}:${mode}`;
            if (loadedUrlKey.current === key) return;
            loadInvoiceIntoPanel(urlInvoiceId, mode);
            return;
        }

        if (loadedUrlKey.current !== "") {
            resetPanelState();
        }
    }, [urlInvoiceId, urlMode, loadInvoiceIntoPanel, applyFormState, resetPanelState]);

    const openPanelView = useCallback(
        (id) => {
            if (!id) return;
            writeInvoiceUrl({ id });
        },
        [writeInvoiceUrl]
    );

    const closePanel = useCallback(() => {
        resetPanelState();
        writeInvoiceUrl({}, { replace: true });
    }, [resetPanelState, writeInvoiceUrl]);

    const startEditInPanel = () => {
        if (!panelInvoice?._id) return;
        applyFormState(buildFormFromInvoice(panelInvoice));
        writeInvoiceUrl({ id: panelInvoice._id, mode: "edit" });
    };

    const finishCancelEdit = () => {
        if (panelMode === "create") {
            closePanel();
            return;
        }
        if (editingId) {
            loadedUrlKey.current = "";
            writeInvoiceUrl({ id: editingId });
        } else {
            setPanelMode("view");
        }
    };

    const requestDismiss = (action) => {
        if (isEditing && isDirty) {
            setPendingDismiss(action);
            setDiscardDialogOpen(true);
            return;
        }
        if (action === "close") closePanel();
        else finishCancelEdit();
    };

    const confirmDiscard = () => {
        setDiscardDialogOpen(false);
        const action = pendingDismiss;
        setPendingDismiss(null);
        if (action === "close") closePanel();
        else finishCancelEdit();
    };

    useEffect(() => {
        if (!panelOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            if (discardDialogOpen || deleteDialogOpen) return;
            e.preventDefault();
            requestDismiss(isEditing ? "cancel" : "close");
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: bind while open
    }, [panelOpen, isEditing, isDirty, discardDialogOpen, deleteDialogOpen]);

    const markReceived = useCallback(
        async (id) => {
            if (!id || markingReceivedId) return;
            setMarkingReceivedId(id);
            try {
                const result = await PutData(
                    { received: true, receivedDate: new Date().toISOString() },
                    `broylesllc/invoices/${id}`
                );
                if (result.success) {
                    setSnack({
                        open: true,
                        color: "success",
                        message: "Invoice marked received.",
                    });
                    if (String(editingId) === String(id) || String(panelInvoice?._id) === String(id)) {
                        loadedUrlKey.current = "";
                        writeInvoiceUrl({ id, tab: "received" });
                    }
                    refetch();
                } else {
                    setSnack({
                        open: true,
                        color: "error",
                        message:
                            result?.data?.message ||
                            result?.error?.response?.data?.message ||
                            "Could not mark received.",
                    });
                }
            } finally {
                setMarkingReceivedId(null);
            }
        },
        [editingId, markingReceivedId, panelInvoice?._id, refetch, writeInvoiceUrl]
    );

    const processRowUpdate = useCallback(
        async (newRow, oldRow) => {
            const nextTracking = String(newRow.trackingId || "").trim();
            const prevTracking = String(oldRow.trackingId || "").trim();
            if (nextTracking === prevTracking) {
                return { ...oldRow, trackingId: prevTracking };
            }

            const result = await PutData(
                { trackingId: nextTracking },
                `broylesllc/invoices/${newRow.id}`
            );
            if (!result.success) {
                const message =
                    result?.data?.message ||
                    result?.error?.response?.data?.message ||
                    "Could not save tracking ID.";
                setSnack({ open: true, color: "error", message });
                throw new Error(message);
            }

            const updated = {
                ...newRow,
                trackingId: nextTracking,
                raw: { ...newRow.raw, trackingId: nextTracking },
            };
            setRows((prev) =>
                prev.map((row) =>
                    String(row.id) === String(newRow.id) ? updated : row
                )
            );
            if (panelInvoice && String(panelInvoice._id) === String(newRow.id)) {
                setPanelInvoice((prev) =>
                    prev ? { ...prev, trackingId: nextTracking } : prev
                );
                if (isEditing) {
                    setForm((prev) => {
                        const next = { ...prev, trackingId: nextTracking };
                        setFormBaseline(editableFormSnapshot(next));
                        return next;
                    });
                }
            }
            setSnack({ open: true, color: "success", message: "Tracking ID saved." });
            return updated;
        },
        [isEditing, panelInvoice]
    );

    const baseColumns = useMemo(
        () => {
            const invoiceNumberCol = {
                field: "invoiceNumber",
                headerName: "Invoice #",
                minWidth: 130,
                flex: 0.8,
                renderCell: (params) => {
                    const label = params.value || "";
                    if (!label) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={(e) => {
                                e.stopPropagation();
                                openPanelView(params.id);
                            }}
                            sx={{
                                fontSize: "inherit",
                                textAlign: "left",
                                cursor: "pointer",
                                fontWeight: 600,
                                color: "#0d9488",
                            }}
                        >
                            {label}
                        </Link>
                    );
                },
            };

            const uploadedDateCol = {
                field: "uploadedDate",
                headerName: "Uploaded Date",
                minWidth: 120,
                valueGetter: (_value, row) => row.uploadedDateRaw || row.raw?.createdAt || null,
                valueFormatter: (value) => formatDateDisplay(value) || "—",
            };

            const sharedTail = [
                {
                    field: "supplierName",
                    headerName: "Supplier",
                    minWidth: 160,
                    flex: 1,
                },
                {
                    field: "deliveryStatus",
                    headerName: "Delivery",
                    minWidth: 150,
                    flex: 0.9,
                    renderCell: (params) => {
                        const text = params.value || "";
                        if (!text) {
                            return (
                                <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                    —
                                </Typography>
                            );
                        }
                        return <Chip size="small" label={text} sx={deliveryStatusChipSx(text)} />;
                    },
                },
                {
                    field: "trackingId",
                    headerName: "Tracking ID",
                    minWidth: 160,
                    flex: 0.8,
                    editable: true,
                    renderCell: (params) => {
                        const text = params.value || "";
                        if (!text) {
                            return (
                                <Typography
                                    component="span"
                                    sx={{ color: "#9aa3ad", fontSize: 13 }}
                                >
                                    Double-click to edit
                                </Typography>
                            );
                        }
                        return text;
                    },
                },
                {
                    field: "numberOfItemsOnInvoice",
                    headerName: "Items",
                    width: 80,
                    align: "right",
                    headerAlign: "right",
                },
                {
                    field: "totalInvoiceAmount",
                    headerName: "Total",
                    width: 110,
                    align: "right",
                    headerAlign: "right",
                    valueFormatter: (value) => formatMoney(value) || "—",
                },
                {
                    field: "lineItemStatuses",
                    headerName: "Line item statuses",
                    minWidth: 160,
                    flex: 1,
                },
            ];

            if (isIncomingTab) {
                return [
                {
                    field: "markReceived",
                    headerName: "Received",
                    width: 120,
                    sortable: false,
                    filterable: false,
                    hideable: false,
                    renderCell: (params) => (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                    markingReceivedId === params.id ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <CheckCircleOutlineOutlinedIcon fontSize="small" />
                                    )
                                }
                                disabled={Boolean(markingReceivedId)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    markReceived(params.id);
                                }}
                                sx={{
                                    ...catalogQuietButtonSx,
                                    minWidth: 0,
                                    px: 1,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#0f766e",
                                    borderColor: "#99f6e4",
                                    backgroundColor: "#ecfdf5",
                                    "&:hover": {
                                        borderColor: "#5eead4",
                                        backgroundColor: "#ccfbf1",
                                    },
                                }}
                            >
                                Received
                            </Button>
                        ),
                    },
                    invoiceNumberCol,
                    {
                        field: "invoiceDate",
                        headerName: "Ordered",
                        minWidth: 115,
                    },
                    uploadedDateCol,
                    {
                        field: "daysSinceOrdered",
                        headerName: "Days since ordered",
                        minWidth: 150,
                        align: "right",
                        headerAlign: "right",
                        renderCell: (params) => {
                            const days = params.value;
                            if (days == null || days === "") {
                                return (
                                    <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                        —
                                    </Typography>
                                );
                            }
                            const overdue = Number(days) > 30;
                            return (
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 13.5,
                                        fontWeight: overdue ? 700 : 600,
                                        color: overdue ? "#dc2626" : "#1c2430",
                                    }}
                                >
                                    {days}
                                </Typography>
                            );
                        },
                    },
                    ...sharedTail,
                ];
            }

            return [
                invoiceNumberCol,
                {
                    field: "receivedDate",
                    headerName: "Received date",
                    minWidth: 120,
                },
                {
                    field: "invoiceDate",
                    headerName: "Ordered",
                    minWidth: 115,
                },
                uploadedDateCol,
                ...sharedTail,
            ];
        },
        [isIncomingTab, markReceived, markingReceivedId, openPanelView]
    );

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.committedWidths, layout.applyWidths]
    );

    const deleteTargets = useMemo(
        () =>
            selectedIds.map((id) => {
                const row = rows.find((r) => String(r.id) === String(id));
                return {
                    id,
                    invoiceNumber: row?.invoiceNumber || String(id),
                };
            }),
        [selectedIds, rows]
    );

    const setField = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const setSelect = (key) => (value) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const save = async () => {
        if (!form.invoiceNumber.trim()) {
            setSnack({
                open: true,
                color: "error",
                message: "Invoice number is required.",
            });
            return;
        }
        setSaving(true);
        const payload = {
            invoiceNumber: form.invoiceNumber.trim(),
            supplierName: form.supplierName,
            deliveryStatus: form.deliveryStatus,
            received: Boolean(form.received),
            receivedDate: form.received
                ? form.receivedDate
                    ? new Date(form.receivedDate).toISOString()
                    : new Date().toISOString()
                : null,
            trackingId: String(form.trackingId || "").trim(),
            invoiceProcessedBy: form.invoiceProcessedBy,
            invoiceDate: form.invoiceDate
                ? new Date(form.invoiceDate).toISOString()
                : null,
            invoiceAutomationUploadDate: form.invoiceAutomationUploadDate
                ? new Date(form.invoiceAutomationUploadDate).toISOString()
                : null,
        };

        const result = editingId
            ? await PutData(payload, `broylesllc/invoices/${editingId}`)
            : await PostData(payload, "broylesllc/invoices");

        setSaving(false);
        if (result.success) {
            const savedId = result.data?._id || editingId;
            const savedTab = result.data?.received || payload.received ? "received" : "incoming";
            setSnack({
                open: true,
                color: "success",
                message: editingId ? "Invoice updated." : "Invoice created.",
            });
            refetch();
            setSelectionModel({ type: "include", ids: new Set() });
            if (savedId) {
                loadedUrlKey.current = "";
                writeInvoiceUrl({ id: savedId, tab: savedTab });
            } else {
                closePanel();
            }
        } else {
            const msg =
                result?.data?.message ||
                result?.error?.response?.data?.message ||
                "Save failed.";
            setSnack({ open: true, color: "error", message: msg });
        }
    };

    const refreshRollups = async () => {
        const id = editingId || panelInvoice?._id;
        if (!id) return;
        setRefreshingRollups(true);
        try {
            const { success, data, error } = await PostData(
                {},
                `broylesllc/invoices/${id}/refresh-rollups`,
                null
            );
            if (success && data) {
                setPanelInvoice(data);
                if (isEditing) applyFormState(buildFormFromInvoice(data));
                setSnack({
                    open: true,
                    color: "success",
                    message: "Totals updated from catalog line items.",
                });
                refetch();
            } else {
                setSnack({
                    open: true,
                    color: "error",
                    message: error?.response?.data?.message || "Refresh failed.",
                });
            }
        } finally {
            setRefreshingRollups(false);
        }
    };

    const confirmDelete = async () => {
        if (!selectedIds.length) return;
        setDeleteDialogOpen(false);
        setDeleting(true);
        try {
            await Promise.all(
                selectedIds.map((id) => api.delete(`api/broylesllc/invoices/${id}`))
            );
            setSnack({ open: true, color: "success", message: "Invoice(s) deleted." });
            setSelectionModel({ type: "include", ids: new Set() });
            if (selectedIds.some((id) => String(id) === String(editingId))) {
                closePanel();
            }
            refetch();
        } catch (err) {
            setSnack({
                open: true,
                color: "error",
                message: err?.response?.data?.message || "Delete failed.",
            });
        } finally {
            setDeleting(false);
        }
    };

    function ViewBody({ inv }) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <DetailField label="Supplier" value={inv.supplierName} />
                <DetailField label="Delivery">
                    {inv.deliveryStatus ? (
                        <Chip
                            size="small"
                            label={inv.deliveryStatus}
                            sx={{ ...deliveryStatusChipSx(inv.deliveryStatus), mt: 0.25 }}
                        />
                    ) : (
                        <Typography sx={{ fontSize: 15, color: "#1c2430" }}>—</Typography>
                    )}
                </DetailField>
                <DetailField label="Received" value={inv.received ? "Yes" : "No"} />
                <DetailField
                    label="Received date"
                    value={formatDateDisplay(inv.receivedDate)}
                />
                <DetailField label="Tracking ID" value={inv.trackingId} />
                <DetailField label="Invoice date" value={formatDateDisplay(inv.invoiceDate)} />
                <DetailField
                    label="Processed on"
                    value={formatDateDisplay(inv.invoiceAutomationUploadDate)}
                />
                <DetailField label="Processed by" value={inv.invoiceProcessedBy} />
                {inv.invoicePhoto?.[0]?.gcsObject || inv.invoicePhoto?.[0]?.sourceUrl ? (
                    <Box sx={{ py: 0.75 }}>
                        <Link
                            component="button"
                            type="button"
                            onClick={() => openInvoicePdfPreview(inv)}
                            sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                p: 0,
                                font: "inherit",
                            }}
                        >
                            Open invoice PDF
                        </Link>
                    </Box>
                ) : null}

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>
                    Summary from line items
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#5c6570", mb: 1 }}>
                    Calculated from linked catalog items. Edit links in Catalog Items when
                    needed.
                </Typography>
                <LinkedCatalogItems
                    catalogItems={inv.catalogItems}
                    totalAmount={inv.totalInvoiceAmount}
                    formatMoney={formatMoney}
                    invoiceId={inv._id}
                />
                <DetailField
                    label="Line item statuses"
                    value={joinList(inv.statusRollupForAutomation)}
                />
                <DetailField
                    label="Purchase dates on line items"
                    value={joinList(
                        (inv.invoiceDateRollup || []).map((d) => formatDateDisplay(d))
                    )}
                />
                <DetailField
                    label="Supplier lot IDs"
                    value={joinList(inv.parserItemIdsOnInvoice)}
                />
            </Box>
        );
    }

    function EditBody() {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                    label="Invoice number"
                    size="small"
                    value={form.invoiceNumber}
                    onChange={setField("invoiceNumber")}
                    fullWidth
                    required
                />
                <SingleSelectField
                    label="Supplier"
                    value={form.supplierName}
                    onChange={setSelect("supplierName")}
                    choices={invoiceMeta.supplierName?.choices || []}
                />
                <SingleSelectField
                    label="Delivery status"
                    value={form.deliveryStatus}
                    onChange={setSelect("deliveryStatus")}
                    choices={invoiceMeta.deliveryStatus?.choices || []}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={Boolean(form.received)}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    received: e.target.checked,
                                    receivedDate: e.target.checked
                                        ? prev.receivedDate || formatDateInput(new Date())
                                        : "",
                                }))
                            }
                        />
                    }
                    label="Received"
                />
                <TextField
                    label="Received date"
                    size="small"
                    type="date"
                    value={form.receivedDate}
                    onChange={setField("receivedDate")}
                    fullWidth
                    disabled={!form.received}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="Tracking ID"
                    size="small"
                    value={form.trackingId}
                    onChange={setField("trackingId")}
                    fullWidth
                />
                <TextField
                    label="Invoice date"
                    size="small"
                    type="date"
                    value={form.invoiceDate}
                    onChange={setField("invoiceDate")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="Processed on"
                    size="small"
                    type="date"
                    value={form.invoiceAutomationUploadDate}
                    onChange={setField("invoiceAutomationUploadDate")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="Processed by"
                    size="small"
                    value={form.invoiceProcessedBy}
                    onChange={setField("invoiceProcessedBy")}
                    fullWidth
                />
                {form.invoicePhoto?.gcsObject || form.invoicePhoto?.sourceUrl ? (
                    <Link
                        component="button"
                        type="button"
                        onClick={() =>
                            openInvoicePdfPreview(
                                form.invoicePhoto,
                                `Invoice ${form.invoiceNumber || ""}`.trim() || "Invoice PDF"
                            )
                        }
                        sx={{
                            fontSize: 14,
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            p: 0,
                            font: "inherit",
                            textAlign: "left",
                        }}
                    >
                        Open invoice PDF
                    </Link>
                ) : null}

                {panelMode === "edit" ? (
                    <>
                        <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
                            Summary from line items
                        </Typography>
                        <DetailField label="Line item count" value={form.numberOfItemsOnInvoice} />
                        <DetailField
                            label="Total amount"
                            value={formatMoney(form.totalInvoiceAmount)}
                        />
                        <DetailField label="Line item statuses" value={form.statusRollupForAutomation} />
                        <DetailField
                            label="Purchase dates on line items"
                            value={form.invoiceDateRollup}
                        />
                        <DetailField label="Supplier lot IDs" value={form.parserItemIdsOnInvoice} />
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                                refreshingRollups ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <SyncOutlinedIcon fontSize="small" />
                                )
                            }
                            disabled={refreshingRollups}
                            onClick={refreshRollups}
                            sx={{ ...catalogQuietButtonSx, alignSelf: "flex-start" }}
                        >
                            Update totals from catalog
                        </Button>
                    </>
                ) : null}
            </Box>
        );
    }

    const panelTitle =
        panelMode === "create"
            ? "New invoice"
            : panelMode === "edit"
              ? panelInvoice?.invoiceNumber
                  ? `Edit ${panelInvoice.invoiceNumber}`
                  : "Edit invoice"
              : panelInvoice?.invoiceNumber
                ? `Invoice ${panelInvoice.invoiceNumber}`
                : "Invoice";

    return (
        <>
            {error && (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 0.5 }}>
                    Failed to load invoices.
                </Typography>
            )}
            <div className="app-page-stack">
                <div style={{ flexShrink: 0 }}>
                    <TabNavigation
                        value={urlTab}
                        onChange={changeTab}
                        tabs={INVOICE_TABS}
                    />
                </div>
                <div className="catalog-grid-shell">
                    <div className="catalog-toolbar">
                        <TextField
                            size="small"
                            placeholder="Search invoice #, supplier, tracking…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            sx={catalogSearchFieldSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <span className="catalog-selection-hint">
                            {selectedIds.length > 0
                                ? `${selectedIds.length} selected`
                                : `${filterActive ? filteredRows.length : rowCount} ${
                                      isIncomingTab ? "incoming" : "received"
                                  }`}
                        </span>
                        <div className="catalog-toolbar-spacer" />
                        <GridLayoutControls layout={layout} columns={baseColumns} />
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!selectedIds.length || deleting}
                            startIcon={
                                deleting ? (
                                    <CircularProgress size={14} color="error" />
                                ) : (
                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                )
                            }
                            onClick={() => setDeleteDialogOpen(true)}
                            sx={catalogDangerButtonSx}
                        >
                            Delete
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddOutlinedIcon fontSize="small" />}
                            onClick={() => navigate("/invoice-file-upload")}
                            sx={catalogPrimaryButtonSx}
                        >
                            Add invoice
                        </Button>
                    </div>
                    <GridFilterBar {...filterBar} />
                    <div className="catalog-grid-main">
                        <DataGrid
                            {...appDataGridDefaultProps}
                            loading={Boolean(load || isLoading)}
                            rows={filteredRows}
                            columns={columns}
                            checkboxSelection
                            disableRowSelectionOnClick
                            rowSelectionModel={selectionModel}
                            onRowSelectionModelChange={setSelectionModel}
                            columnVisibilityModel={layout.columnVisibilityModel}
                            onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                            onColumnWidthChange={layout.onColumnWidthChange}
                            onCellClick={(params, event) => {
                                if (
                                    params.field === "trackingId" ||
                                    params.field === "markReceived" ||
                                    params.field === "__check__"
                                ) {
                                    event.stopPropagation();
                                    return;
                                }
                                openPanelView(params.id);
                            }}
                            onCellDoubleClick={(params, event) => {
                                if (params.field !== "trackingId") return;
                                event.defaultMuiPrevented = true;
                                event.stopPropagation();
                                if (params.api.getCellMode(params.id, params.field) === "edit") {
                                    return;
                                }
                                params.api.startCellEditMode({
                                    id: params.id,
                                    field: params.field,
                                });
                            }}
                            processRowUpdate={processRowUpdate}
                            onProcessRowUpdateError={() => {}}
                            getRowClassName={(params) =>
                                activePanelId && String(params.id) === String(activePanelId)
                                    ? "invoice-panel-active-row"
                                    : ""
                            }
                            sortingMode="server"
                            sortModel={sortModel}
                            onSortModelChange={(next) => {
                                layout.onSortModelChange(next);
                                setPaginationModel((prev) =>
                                    prev.page === 0 ? prev : { ...prev, page: 0 }
                                );
                            }}
                            paginationMode={
                                filterActive && !Object.keys(uploadedDateQuery).length
                                    ? "client"
                                    : "server"
                            }
                            rowCount={
                                filterActive && !Object.keys(uploadedDateQuery).length
                                    ? filteredRows.length
                                    : rowCount
                            }
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[25, 50, 100]}
                            slotProps={appDataGridSlotProps}
                            sx={invoiceGridSx}
                            localeText={{
                                noRowsLabel: isIncomingTab
                                    ? "No incoming invoices."
                                    : "No received invoices yet.",
                            }}
                        />
                    </div>
                </div>
            </div>

            <Dialog
                open={panelOpen}
                onClose={() => requestDismiss(isEditing ? "cancel" : "close")}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: {
                        borderRadius: "14px",
                        maxHeight: "min(900px, calc(100vh - 48px))",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                        pr: 6,
                        borderBottom: "1px solid #eee",
                        position: "relative",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <span>{panelTitle}</span>
                        {panelMode === "view" && panelInvoice?.deliveryStatus ? (
                            <Chip
                                size="small"
                                label={panelInvoice.deliveryStatus}
                                sx={deliveryStatusChipSx(panelInvoice.deliveryStatus)}
                            />
                        ) : null}
                        {panelMode === "edit" ? (
                            <Chip
                                size="small"
                                label="Editing"
                                sx={{
                                    height: 24,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    borderRadius: "8px",
                                    backgroundColor: "#ecfdf5",
                                    color: "#0f766e",
                                }}
                            />
                        ) : null}
                        {isDirty ? (
                            <Chip
                                size="small"
                                label="Unsaved changes"
                                sx={{
                                    height: 24,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    backgroundColor: "#fff7ed",
                                    color: "#c2410c",
                                }}
                            />
                        ) : null}
                    </Box>
                    {panelMode === "view" && panelInvoice?.supplierName ? (
                        <Typography sx={{ fontSize: 13, color: "#5c6570", mt: 0.5, fontWeight: 500 }}>
                            {panelInvoice.supplierName}
                            {panelInvoice.totalInvoiceAmount != null
                                ? ` · ${formatMoney(panelInvoice.totalInvoiceAmount)}`
                                : ""}
                        </Typography>
                    ) : null}
                    <Box
                        sx={{
                            position: "absolute",
                            right: 12,
                            top: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                        }}
                    >
                        {panelMode === "view" && panelInvoice && !panelLoading ? (
                            <Button
                                size="small"
                                startIcon={<EditOutlinedIcon fontSize="small" />}
                                onClick={startEditInPanel}
                                sx={catalogQuietButtonSx}
                            >
                                Edit
                            </Button>
                        ) : null}
                        <IconButton
                            size="small"
                            onClick={() => requestDismiss(isEditing ? "cancel" : "close")}
                            aria-label="Close"
                        >
                            <CloseOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{ px: 3, py: 2 }}>
                    {panelLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : panelMode === "view" && panelInvoice ? (
                        <ViewBody inv={panelInvoice} />
                    ) : isEditing ? (
                        <EditBody />
                    ) : null}
                </DialogContent>

                {isEditing ? (
                    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                        <Button
                            onClick={() => requestDismiss("cancel")}
                            sx={catalogQuietButtonSx}
                        >
                            {panelMode === "create" ? "Discard" : "Cancel"}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={saving}
                            onClick={save}
                            startIcon={
                                saving ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : null
                            }
                            sx={catalogPrimaryButtonSx}
                        >
                            {panelMode === "create" ? "Create invoice" : "Save changes"}
                        </Button>
                    </DialogActions>
                ) : null}
            </Dialog>

            <Dialog
                open={discardDialogOpen}
                onClose={() => setDiscardDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Discard unsaved changes?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14, color: "#5c6570" }}>
                        You have edits that are not saved. If you leave now, those changes will be
                        lost.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDiscardDialogOpen(false)} sx={catalogQuietButtonSx}>
                        Keep editing
                    </Button>
                    <Button onClick={confirmDiscard} sx={catalogDangerButtonSx}>
                        Discard changes
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                confirming={deleting}
                title={
                    deleteTargets.length === 1
                        ? "Delete invoice?"
                        : `Delete ${deleteTargets.length} invoices?`
                }
                description="This cannot be undone. The following will be removed:"
                items={deleteTargets.map((t) => ({
                    key: t.id,
                    label: `Invoice ${t.invoiceNumber}`,
                }))}
            />

            {snack.open && (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            )}

            <FloatingAssetPreview
                open={pdfPreview.open}
                asset={pdfPreview.asset}
                title={pdfPreview.title}
                onClose={() =>
                    setPdfPreview({
                        open: false,
                        asset: null,
                        title: "",
                    })
                }
            />
        </>
    );
}
